// @owners { team: patients-team }
import { ActionSheetContext, ActionSheetV2, Button, Toast, ToastContext } from '@alto/design-system';
import { type ShipmentTip } from '@alto/scriptdash/alto/orders/types/v1/shipment_tip';
import React, { useContext, useState } from 'react';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE } from '~shared/constants';
import { SOURCES } from '~shared/features/courier-tips/AnalyticsProps';
// eslint-disable-next-line import/no-deprecated
import { createShipmentTip, updateShipmentTip } from '~shared/features/courier-tips/actions';
import sendTipAnalyticsEvent from '~shared/features/courier-tips/actions/sendTipAnalyticsEvent';
import { COURIER_TIP_COPY, DEFAULT_TIP_AMOUNT } from '~shared/features/courier-tips/constants';
import { canSubmitCourierTip } from '~shared/features/courier-tips/helpers';
import { getShipmentTipForShipmentID } from '~shared/features/courier-tips/selectors';
import { getShipmentAddress } from '~shared/features/shipments/selectors/getShipments';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { formatDollars } from '~shared/helpers/currency';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useOrderPricing } from '~shared/queries';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import EditOrAddTip from './EditOrAddTip';

export type Props = {
  readonly shipmentID: number;
};

export const EditOrAddTipModal = ({ shipmentID }: Props) => {
  const { addToast } = useContext(ToastContext);
  const { closeActionSheet } = useContext(ActionSheetContext);
  const currentUserID = useSelectorShared(getCurrentUserID);
  const shipmentTip = useSelectorShared((state) => getShipmentTipForShipmentID(state, { shipmentID }));
  const [tipAmount, setTipAmount] = useState(shipmentTip?.amount ?? DEFAULT_TIP_AMOUNT);
  const [paymentMethodID, setPaymentMethodID] = useState(shipmentTip?.payment_method_id);
  const canSubmitTip = canSubmitCourierTip(tipAmount, paymentMethodID);
  const dispatch = useDispatchShared();
  const address = useSelectorShared((state) => getShipmentAddress(state, { shipmentID }));
  const { refetchOrderPricing } = useOrderPricing({ shipmentID });

  const analyticsProps = {
    shipment_id: shipmentID,
    source: SOURCES.EDIT_DELIVERY,
    facility_id: address?.dispensing_facility_id,
  };

  const onCloseModal = () => {
    dispatch(sendTipAnalyticsEvent({ eventKey: EVENTS.COURIER_TIPS__EDIT_TIP_DISMISSED, analyticsProps }));
    closeActionSheet();
  };

  const handleSuccess = (shipmentTip: ShipmentTip) => {
    dispatch(
      sendTipAnalyticsEvent({
        eventKey: EVENTS.COURIER_TIPS__TIP_UPDATED,
        analyticsProps,
        additionalProps: {
          tip_value: formatDollars(shipmentTip.amount, false),
          tip_payment_method_id: shipmentTip.payment_method_id,
        },
      }),
    );
    addToast(<Toast>{COURIER_TIP_COPY.TOAST_SUCCESSFUL_UPDATE}</Toast>);
    closeActionSheet();
  };

  const handleError = () => {
    addToast(<Toast variant="error">{`${DEFAULT_ERROR_ALERT_TITLE} ${DEFAULT_ERROR_ALERT_MESSAGE}`}</Toast>);
  };

  const handleAddTip = async (paymentMethodID: number, currentUserID: number) => {
    const request = {
      user_id: currentUserID,
      shipment_id: shipmentID,
      payment_method_id: paymentMethodID,
      amount: tipAmount,
    };

    try {
      // eslint-disable-next-line import/no-deprecated
      const createdShipmentTip = await dispatch(createShipmentTip(request));
      if (createdShipmentTip) {
        handleSuccess(createdShipmentTip);
      }
      refetchOrderPricing();
    } catch {
      handleError();
    }
  };

  const handleUpdateTip = async (tipAmount: number, paymentMethodID: number) => {
    if (!shipmentTip) return;
    const editShipmentTip = {
      ...shipmentTip,
      payment_method_id: paymentMethodID,
      amount: tipAmount,
    };
    const request = {
      id: shipmentTip.id,
      shipment_tip: editShipmentTip,
    };

    try {
      // eslint-disable-next-line import/no-deprecated
      const updatedShipmentTip = await dispatch(updateShipmentTip(request));
      if (updatedShipmentTip) {
        handleSuccess(updatedShipmentTip);
      }
      refetchOrderPricing();
    } catch {
      handleError();
    }
  };

  const handleUpsertTip = () => {
    if (shipmentTip && paymentMethodID) {
      handleUpdateTip(tipAmount, paymentMethodID);
    } else if (paymentMethodID && currentUserID) {
      handleAddTip(paymentMethodID, currentUserID);
    }
  };

  return (
    <ActionSheetV2
      analyticsName="edit or add tip"
      buttons={[
        <Button
          key="submit-tip"
          disabled={!canSubmitTip}
          accessibilityLabel="Submit"
          label="Submit"
          onPress={handleUpsertTip}
        />,
        <Button
          key="dismiss-tip"
          accessibilityLabel="Dismiss"
          label="Dismiss"
          onPress={onCloseModal}
          type="tertiary"
        />,
      ]}
      narrow
    >
      <EditOrAddTip
        editTipAmount={setTipAmount}
        editTipPaymentMethodID={setPaymentMethodID}
        tipAmount={tipAmount}
        tipPaymentMethodID={paymentMethodID}
        analyticsProps={analyticsProps}
      />
    </ActionSheetV2>
  );
};
